import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { fetchList } from "../api";
import { PageHero, Loader } from "../components/Shared";

export default function Roster() {
  const [wrestlers, setWrestlers] = useState(null);
  const [honorees, setHonorees] = useState([]);

  useEffect(() => { fetchList("wrestlers").then(setWrestlers).catch(() => setWrestlers([])); }, []);
  useEffect(() => { fetchList("honorees").then(setHonorees).catch(() => setHonorees([])); }, []);

  if (!wrestlers) return <Loader />;

  return (
    <div data-testid="roster-page">
      <PageHero kicker="Superstars" title="The Roster" image="https://images.unsplash.com/photo-1636391134068-083dd5e3209b?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000" />
      <section className="px-6 md:px-12 lg:px-20 pb-24">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {wrestlers.map((w, i) => (
            <motion.div key={w.id} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 8) * 0.06 }}>
              <Link to={`/roster/${w.id}`} data-testid={`roster-card-${w.id}`} className="relative group block border border-zinc-800 overflow-hidden aspect-[3/4]">
                <img src={w.image} alt={w.name} style={{ objectPosition: w.card_pos || (w.image_pos === "center" ? "center" : w.image_pos === "bottom" ? "bottom" : "top"), transform: w.card_zoom && w.card_zoom !== 1 ? `scale(${w.card_zoom})` : undefined }} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-transparent" />
                <div className="absolute bottom-0 p-5">
                  <div className="w-6 h-[2px] bg-brand mb-2" />
                  <h3 className="font-display text-3xl uppercase leading-none">{w.name}</h3>
                  {w.nickname && <p className="text-brand text-xs font-mono uppercase tracking-wider mt-1">{w.nickname}</p>}
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        {wrestlers.length === 0 && <p className="text-zinc-500">Roster coming soon.</p>}
      </section>

      {/* HALL OF HONOR */}
      {honorees.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 pb-24" data-testid="hall-of-honor">
          <div className="border-t-2 border-brand pt-16">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-[2px] bg-brand" />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand">A Place Of Legacy</span>
            </div>
            <h2 className="font-display text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tight leading-none">Hall of Honor</h2>
            <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
              The CWF Hall of Honor celebrates the men and women who have shaped professional wrestling — icons whose grit, passion, and dedication paved the way for the next generation.
            </p>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {honorees.map((h, i) => (
                <motion.div key={h.id} data-testid={`honoree-card-${h.id}`}
                  initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: (i % 6) * 0.06 }}
                  className="border border-zinc-800 bg-black overflow-hidden group">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={h.image} alt={h.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <span className="font-mono text-xs uppercase tracking-widest text-brand">Inductee</span>
                    <h3 className="font-display text-4xl uppercase tracking-tight leading-none mt-2">{h.name}</h3>
                    {h.blurb && <p className="text-zinc-400 mt-4 leading-relaxed text-sm">{h.blurb}</p>}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
